import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Head from 'next/head';

import Link from 'components/Link';
import SocialShareInline from 'components/SocialShareInline';
import VideoPlayer from 'components/VideoPlayer';

import { loadRecommendations as loadRecommendationsAction } from 'redux/modules/recommended';
import { loadVideosByIds as loadVideosByIdsAction } from 'redux/modules/video/associatedPlaylists';

import libAmp from 'lib/amp';
import Breakpoints from 'lib/Breakpoints';
import BTE from 'lib/BTE';
import {
  autoplayCapabilities as autoplayCapabilitiesPropType,
  video as videoPropType,
} from 'lib/CustomPropTypes';
import { getFormattedDateString, formatDuration } from 'lib/DateTime';
import getNavbarHeight from 'lib/getNavbarHeight';
import $t from 'lib/trackingService';
import recommendationOptions from 'lib/vilynxOptions';
import { buildVideoObjectLD } from 'lib/LDJson';

import styles from './styles.module.scss';
import socialSharingStyles from './socialSharingStyles.module.scss';

const mapStateToProps = ({
  shared, router, amp, recommended,
}) => ({
  vertical: shared.vertical,
  domain: router.domain,
  ampVertical: amp.vertical,
  recommendations: recommended.recommendations,
});

const mapActionsToProps = {
  loadRecommendations: loadVideosByIdsAction,
  loadVideosByIds: loadRecommendationsAction,
};

function videoUrl(video) {
  return video && video.url && video.url.primary;
}

function enrichedVideosKey(video) {
  return `enriched_${videoUrl(video)}`;
}

function recommendationsKey(video) {
  return videoUrl(video);
}

@connect(mapStateToProps, mapActionsToProps)
@libAmp((props) => {
  const {
    video,
    domain,
    isHero,
  } = props;

  let { ampVertical: vertical } = props;
  if (vertical === 'knowyourvalue') {
    vertical = 'know-your-value';
  }

  /* eslint-disable react/jsx-indent */
  return (
    <div className={classNames({ [styles.ampWrapper]: !isHero })} data-test="inline-video__amp">
      <amp-iframe
        sandbox="allow-scripts allow-same-origin"
        layout="responsive"
        frameborder="0"
        width="480"
        height="270"
        src={`https://www3.${domain}/${vertical}/embedded-video/${video.id}#amp=1`}
      >
        <amp-img
          layout="fill"
          width="480"
          height="270"
          src={video.primaryImage.url.primary}
          placeholder
        />
      </amp-iframe>
    </div>
  );
  /* eslint-enable react/jsx-indent */
})
class InlineVideo extends Component {
  static propTypes = {
    autoPlay: PropTypes.bool,
    autoplayCapabilities: autoplayCapabilitiesPropType,
    isEmbedded: PropTypes.bool,
    isHero: PropTypes.bool,
    loadRecommendations: PropTypes.func.isRequired,
    loadVideosByIds: PropTypes.func.isRequired,
    mute: PropTypes.bool,
    onlyAutoplayMuted: PropTypes.bool,
    recommendations: PropTypes.objectOf(PropTypes.any),
    vertical: PropTypes.string.isRequired,
    video: videoPropType.isRequired,
  };

  static contextTypes = {
    isLiveBlog: PropTypes.bool,
  };

  static defaultProps = {
    autoPlay: false,
    autoplayCapabilities: null,
    isEmbedded: false,
    isHero: false,
    recommendations: {},
    mute: false,
    onlyAutoplayMuted: false,
  };

  static getDerivedStateFromProps = ({ recommendations, video }, state) => {
    const enrichedVideos = recommendations && recommendations[enrichedVideosKey(video)];
    if (enrichedVideos && !state.associatedPlaylist) {
      return {
        associatedPlaylist: enrichedVideos,
      };
    }
    return null;
  }

  constructor(props) {
    super(props);

    const { video } = this.props;

    this.state = {
      associatedPlaylist: video && video.associatedVideoPlaylist,
      fetchPlaylistData: false,
      infoHidden: false,
      nowPlaying: video,
      videoPlaying: false,
    };
  }

  componentDidMount() {
    this.initAnalytics();

    if (this.isDefaultInlineVideo(this.props)) {
      BTE.on('scroll', this.monitorDefaultInlineVideos, 'throttle');
    }
  }

  componentDidUpdate() {
    const { fetchPlaylistData } = this.state;
    const { video } = this.props;
    const recommendedVideoIds = this.recommendedVideoIds();
    if (fetchPlaylistData && recommendedVideoIds.length > 0) {
      this.fetchEnrichedVideoRecommendationsByIdsForVideo(
        recommendedVideoIds,
        video,
      );
    }
  }

  componentWillUnmount() {
    BTE.remove('scroll', this.monitorDefaultInlineVideos, 'throttle');
  }

  onAdPlay = () => {
    this.setState({
      infoHidden: true,
    });
  }

  onVideoPlay = () => {
    const { associatedPlaylist } = this.state;
    const { video, vertical } = this.props;
    if (!associatedPlaylist && vertical !== 'noticias') {
      this.fetchRecommendations(videoUrl(video));
    }

    this.setState({
      videoPlaying: true,
      infoHidden: true,
    });
  }

  onVideoPause = () => {
    this.setState({
      videoPlaying: false,
      infoHidden: false,
    });
  }

  onVideoEnd = (selected) => {
    this.setState({
      nowPlaying: selected,
    });
  }

  onMouseMove = () => {
    const { infoHidden, videoPlaying } = this.state;
    if (videoPlaying) {
      if (this.isCaptionsCardOpen() && !infoHidden) {
        this.setState({ infoHidden: true });
      } else if (!this.isCaptionsCardOpen() && infoHidden) {
        this.setState({ infoHidden: false });
      }
    }
  }

  onMouseLeave = () => {
    const { videoPlaying } = this.state;
    if (videoPlaying) {
      this.setState({ infoHidden: true });
    }
  }

  recommendedVideoIds = () => {
    const { recommendations, video } = this.props;
    const rawRecommendations = recommendations[recommendationsKey(video)];
    return rawRecommendations ? rawRecommendations.map((vid) => vid.mpxMetadata.id) : [];
  }

  isDefaultInlineVideo = () => {
    const { isEmbedded, isHero } = this.props;
    return isEmbedded === false && isHero === false;
  }

  initAnalytics = () => {
    if (typeof $t !== 'undefined') {
      $t('register', ['inlineVideoFullyInView']);
    }
  }

  trackInlineVideoFullyInView = () => {
    if (typeof $t !== 'undefined' && !this.fired) {
      $t('track', 'inlineVideoFullyInView');

      this.fired = true;
    }
  }

  monitorDefaultInlineVideos = () => {
    if (!this.wrapper) {
      return;
    }
    const depth = window.pageYOffset;
    const navBarHeight = getNavbarHeight(Breakpoints);
    const playerWrapper = this.wrapper;
    const { top: containerTop, height: containerHeight } = playerWrapper.getBoundingClientRect();

    const topOffset = containerTop + depth;
    const bottomOffset = topOffset + containerHeight;

    const vidContainerTopShowing = topOffset > (depth + navBarHeight);
    const vidContainerBottomShowing = bottomOffset < (depth + window.innerHeight);

    if (vidContainerTopShowing && vidContainerBottomShowing) {
      this.trackInlineVideoFullyInView();
    }
  }

  isCaptionsCardOpen = () => {
    if (this.player && this.player.wrapper) {
      const captionCard = document.querySelector('.ndpCaptionOptionsCard', this.player.wrapper);
      if (captionCard && (!captionCard.style.display || captionCard.style.display !== 'none')) {
        return true;
      }
    }
    return false;
  }

  play = (e) => {
    const { player } = this;
    if (player && player.manualPlay) {
      player.manualPlay(e);
    }
  }

  fetchEnrichedVideoRecommendationsByIdsForVideo = (videoIds, video) => {
    const { loadVideosByIds } = this.props;
    loadVideosByIds(videoIds, enrichedVideosKey(video));
    this.setState({ fetchPlaylistData: false });
  }

  fetchRecommendations = async (url) => {
    const { loadRecommendations, vertical } = this.props;
    const options = recommendationOptions({ vertical, type: 'video' });
    await loadRecommendations(url, options);
    this.setState({ fetchPlaylistData: true });
  }

  getTitle = () => {
    const { nowPlaying: { headline: { tease }, url } } = this.state;
    const { isEmbedded, vertical } = this.props;

    let titleToolkit = 'f5 f7-m lh-title';
    if (vertical === 'today') {
      titleToolkit = 'f4 f6-m lh-copy';
    }

    return (
      <h2 className={classNames(styles.title, titleToolkit, 'founders-cond ma0 pa0')}>
        {isEmbedded && (
          <button
            type="button"
            className={styles.titleButton}
            onClick={this.play}
          >
            {tease}
          </button>
        )}
        {!isEmbedded
          && <Link to={url.primary || url}>{tease}</Link>}
      </h2>
    );
  }

  getInfo = () => {
    const {
      nowPlaying: {
        nativeAd,
        duration,
        datePublished: date,
      },
    } = this.state;

    return (
      <span className={classNames(styles.infoText, 'f2 ttu mt3')}>
        {nativeAd
          && <span className={styles.sponsored}>Paid Content</span>}
        <span className={styles.date}>{getFormattedDateString(date)}</span>
        {duration && <span>{formatDuration(duration)}</span>}
      </span>
    );
  }

  render() {
    const { nowPlaying: video, associatedPlaylist, infoHidden } = this.state;
    if (!video) return null;
    const {
      isEmbedded, isHero, autoPlay, onlyAutoplayMuted, mute, autoplayCapabilities, vertical,
    } = this.props;
    const { isLiveBlog } = this.context;

    const embeddedStyles = classNames(
      styles.inlineVideo,
      {
        [styles.contentBody]: !isEmbedded && !isHero && !isLiveBlog,
        [styles.isHero]: isHero,
        mv8: !isEmbedded && !isHero && !isLiveBlog,
        mb6: isLiveBlog,
        mt6: isLiveBlog && !isHero,
        'mt0 mt6-m': isHero && isLiveBlog,
      },
    );

    const infoStyles = classNames(
      styles.info,
      'pt4 pl4 pr8 pt5-m pl6-m pr9-m',
      { [styles.infoHidden]: infoHidden },
    );

    const playButtonStyles = classNames(
      styles.playButton,
      {
        [styles.isHero]: isHero,
        [styles.hasRedirect]: !isEmbedded,
      },
    );

    return (
      <section
        className={embeddedStyles}
        onMouseLeave={this.onMouseLeave}
        onMouseMove={this.onMouseMove}
      >
        <div className={styles.background}>
          <div className={styles.player} ref={(ref) => { this.wrapper = ref; }}>
            <VideoPlayer
              autoPlay={autoPlay || false}
              autoplayCapabilities={autoplayCapabilities}
              continuousPlay
              disableSticky={isLiveBlog && !isHero}
              isEmbedded={isEmbedded}
              onlyAutoplayMuted={onlyAutoplayMuted}
              onAdPlay={this.onAdPlay}
              onVideoPlay={this.onVideoPlay}
              onVideoPause={this.onVideoPause}
              onVideoEnd={this.onVideoEnd}
              onRef={(player) => { this.player = player; }}
              playButtonStyle={playButtonStyles}
              playlist={[associatedPlaylist]}
              manualPlay={autoPlay || false}
              mute={mute || false}
              shouldStickWhilePaused={isLiveBlog && isHero}
              vertical={vertical}
              video={video}
            />
          </div>
        </div>
        <div className={infoStyles}>
          {this.getTitle()}
          {this.getInfo()}
          <SocialShareInline
            content={video}
            propStyles={socialSharingStyles}
          />
        </div>
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: JSON.stringify(buildVideoObjectLD(video)),
            }}
          />
        </Head>
      </section>
    );
  }
}

export default InlineVideo;
